/**
 * High-Performance Client-Side Market Data Cache using IndexedDB.
 * Allows instant chart rendering (0ms delay) on page refresh and timeframe switching
 * for large datasets (10,000 to 50,000 candles) via Stale-While-Revalidate (SWR).
 */

const DB_NAME = "minopro_market_cache_v3";

// Cleanup old IndexedDB databases from previous versions
const OLD_DB_NAMES = ["minopro_market_cache_v1", "minopro_market_cache_v2"];
if (typeof window !== "undefined" && window.indexedDB) {
  OLD_DB_NAMES.forEach((oldName) => {
    try {
      window.indexedDB.deleteDatabase(oldName);
    } catch (e) {
      // Ignore errors silently
    }
  });
}
const STORE_NAME = "market_history";
const DB_VERSION = 1;

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof window === "undefined" || !window.indexedDB) {
        return resolve(null);
      }
      try {
        const request = window.indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: "id" });
          }
        };
        request.onsuccess = (event) => {
          resolve(event.target.result);
        };
        request.onerror = (event) => {
          console.warn("[MarketCache] IndexedDB open error:", event.target.error);
          resolve(null);
        };
      } catch (err) {
        console.warn("[MarketCache] IndexedDB init error:", err);
        resolve(null);
      }
    });
  }
  return dbPromise;
}

/**
 * Retrieve cached market data for a given symbol, timeframe, and strategyMode.
 * Each trading system has an isolated cache key to eliminate cross-system contamination.
 * @param {string} symbol - e.g. "XAUUSD"
 * @param {string} timeframe - e.g. "M5"
 * @param {string} strategyMode - e.g. "hma", "smc", "bob_volman", "orb_session", "matrix", "all"
 * @returns {Promise<Object|null>}
 */
export async function getCachedMarketData(symbol, timeframe, strategyMode = "all") {
  try {
    const db = await getDB();
    if (!db) return null;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const mode = (strategyMode || "all").toLowerCase();
        const key = `${symbol}_${timeframe}_${mode}`;
        const request = store.get(key);

        request.onsuccess = () => {
          const result = request.result;
          if (result && result.data) {
            resolve(result.data);
          } else {
            // Backward compatibility fallback to generic key
            const fallbackReq = store.get(`${symbol}_${timeframe}`);
            fallbackReq.onsuccess = () => {
              const fallbackRes = fallbackReq.result;
              resolve(fallbackRes && fallbackRes.data ? fallbackRes.data : null);
            };
            fallbackReq.onerror = () => resolve(null);
          }
        };

        request.onerror = () => {
          resolve(null);
        };
      } catch (e) {
        resolve(null);
      }
    });
  } catch (err) {
    console.warn("[MarketCache] getCachedMarketData error:", err);
    return null;
  }
}

/**
 * Save market data payload into IndexedDB cache for a specific strategyMode.
 * Completely isolates trading system data from one another.
 * @param {string} symbol - e.g. "XAUUSD"
 * @param {string} timeframe - e.g. "M5"
 * @param {number} candleCount - e.g. 10000
 * @param {Object} data - { bars, indicators, signals, smc, forecast, tick }
 * @param {string} strategyMode - e.g. "hma", "smc", "bob_volman", "orb_session", "matrix", "all"
 */
export async function saveCachedMarketData(symbol, timeframe, candleCount, data, strategyMode = "all") {
  if (!data || !data.bars || data.bars.length === 0) return;

  try {
    const db = await getDB();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const mode = (strategyMode || "all").toLowerCase();
        const key = `${symbol}_${timeframe}_${mode}`;

        store.put({
          id: key,
          symbol,
          timeframe,
          strategyMode: mode,
          candleCount,
          data,
          updatedAt: Date.now(),
        });

        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      } catch (e) {
        resolve(false);
      }
    });
  } catch (err) {
    console.warn("[MarketCache] saveCachedMarketData error:", err);
  }
}
